import ROUTE from '@eu/app/router/appModule/names'
import { registerOnboardingSteps } from '@eu/app/composables/useOnboardingProgress.js'
// type-only: keeps the routes → navigation edge out of the runtime module
// graph (see the import-cycle warning in useOnboardingProgress.js)
import type { AppRouteRecord } from '@eu/app/router/appModule/navigation'
import {
  requireDigaBuild,
  requireSubscription,
  requireCorporate,
  requirePrivateInsurance,
  requireResolvedPractice,
  requireStrictlyPublicInsurance,
  resolveSignUpEntry,
  resolveRenewalEntry,
  skipToContextualDownload,
  skipToHukVerification,
  skipToLegacyDownload,
  skipWhenCorporateKnown,
  skipWhenOptimizedFunnel,
  skipWhenPhoneProvided,
  skipWhenStrictlyPrivateInsurance,
  resolvePreSignupEntry,
  returnToApp,
} from '@eu/app/router/appModule/guards'
import ASSESSMENT_ROUTE from '@shared/modules/assessment/router/names.js'

// Routes declare forward and backward navigation through meta. Ordered entries
// select the first matching condition; keyed `next` entries represent view
// intents. Separate records preserve predecessor context for shared views.
const PhoneInputView = () => import('@eu/app/views/PhoneInput.vue')
const DoctorOutreachConsentView = () =>
  import('@eu/app/views/DoctorOutreachConsent.vue')
const routes = [
  {
    path: '/:corporate_key',
    name: ROUTE.CORP_KEY,
    redirect: (to) => ({
      name: ROUTE.SIGN_UP,
      query: {
        corporate: to.params.corporate_key,
        ...to.query,
      },
    }),
  },
  {
    path: '/sign-up',
    name: ROUTE.SIGN_UP,
    component: () => import('@eu/app/views/SignUp.vue'),
    beforeEnter: [
      resolveRenewalEntry,
      resolveSignUpEntry,
      resolvePreSignupEntry,
    ],
    meta: {
      public: true,
      name: 'signup_registration',
      // In the funnel, signup is the last pre-signup step (see the negative
      // pre-signup offsets in useOnboardingProgress.js); no bar otherwise.
      onboardingStep: ({ isPreSubscriptionWebOptimizedFunnel }) =>
        isPreSubscriptionWebOptimizedFunnel ? 0 : null,
      next: [
        {
          target: ROUTE.INSURANCE_SELECTION,
          when: ({ corporate }) => corporate == null,
        },
        {
          target: ROUTE.PRESCRIPTION_QUESTION,
          when: ({ flags }) => flags.webOptimizedFunnel,
        },
        { target: ROUTE.INSURANCE_SELECTION },
      ],
      back: [
        {
          target: ROUTE.NAME_INPUT,
          when: ({ flags }) => flags.webOptimizedFunnel,
        },
      ],
    },
  },
  {
    path: '/name-input',
    name: ROUTE.NAME_INPUT,
    component: () => import('@eu/app/views/NameInput.vue'),
    meta: {
      // pre-signup step — should only be reached within flags.webOptimizedFunnel
      public: true,
      name: 'name_input',
      onboardingStep: ({ isPreSubscriptionWebOptimizedFunnel }) =>
        isPreSubscriptionWebOptimizedFunnel ? -1 : null,
      next: [{ target: ROUTE.SIGN_UP }],
      back: [{ target: ROUTE.INSURANCE_COVERED }],
    },
  },
  {
    path: '/code/question',
    name: ROUTE.CODE_QUESTION,
    component: () => import('@eu/app/views/CodeQuestion.vue'),
    beforeEnter: [requireCorporate, skipWhenStrictlyPrivateInsurance],
    meta: {
      name: 'code_question',
      onboardingStep: ({ isResubscription }) => (isResubscription ? 5 : 3),
      next: {
        hasCode: [{ target: ROUTE.CODE_INPUT }],
        noCode: [
          {
            target: ROUTE.PRESCRIPTION_UPLOAD,
            when: ({ isResubscription }) => isResubscription,
          },
          { target: ROUTE.INSURANCE_SEARCH_PRIMER },
        ],
      },
      back: [
        {
          target: ROUTE.DOCTOR_OUTREACH_PRIMER,
          when: ({ isResubscription }) => isResubscription,
        },
        { target: ROUTE.INSURANCE_COVERED },
      ],
    },
  },
  {
    path: '/prescription/question',
    name: ROUTE.PRESCRIPTION_QUESTION,
    component: () => import('@eu/app/views/PrescriptionQuestion.vue'),
    beforeEnter: [requireStrictlyPublicInsurance],
    meta: {
      name: 'prescription_question',
      onboardingStep: 5,
      next: {
        noPrescription: [{ target: ROUTE.DOCTOR_OUTREACH_PRIMER }],
        hasCode: [{ target: ROUTE.CODE_INPUT }],
        uploadPrescription: [{ target: ROUTE.PRESCRIPTION_UPLOAD }],
      },
    },
  },
  {
    path: '/code/input',
    name: ROUTE.CODE_INPUT,
    component: () => import('@eu/app/views/CodeInput.vue'),
    meta: {
      name: 'unified_code_input',
      onboardingStep: 9,
      next: {
        activated: [{ target: ROUTE.DOWNLOAD_LINK }],
        // a PZN sends the user back into the upload flow
        pzn: [{ target: ROUTE.PRESCRIPTION_UPLOAD }],
      },
      back: [
        {
          target: ROUTE.CODE_QUESTION,
          when: ({ isResubscription }) => isResubscription,
        },
        {
          target: ROUTE.PRESCRIPTION_QUESTION,
          when: ({ flags }) => flags.webOptimizedFunnel,
        },
        { target: ROUTE.CODE_QUESTION },
      ],
    },
  },
  {
    path: '/insurance/search/primer',
    name: ROUTE.INSURANCE_SEARCH_PRIMER,
    component: () => import('@eu/app/views/InsuranceSearchPrimer.vue'),
    beforeEnter: [skipWhenOptimizedFunnel],
    meta: {
      name: 'insurance_search_primer',
      onboardingStep: 4,
      next: [{ target: ROUTE.PRESCRIPTION_CHECK }],
      back: [{ target: ROUTE.CODE_QUESTION }],
    },
  },
  {
    path: '/insurance/selection',
    name: ROUTE.INSURANCE_SELECTION,
    component: () => import('@eu/app/views/InsuranceSelectionExperiment.vue'),
    beforeEnter: [skipWhenCorporateKnown],
    meta: {
      public: true,
      name: 'insurance_selection',
      onboardingStep: ({ isPreSubscriptionWebOptimizedFunnel }) =>
        isPreSubscriptionWebOptimizedFunnel ? -3 : 1,
      next: {
        insuranceSelected: [{ target: ROUTE.INSURANCE_COVERED }],
        supportSkipped: [
          {
            target: ROUTE.DOWNLOAD_LINK,
            when: ({ canReturnToApp, diga }) => canReturnToApp || !diga,
          },
          { target: ROUTE.PRESCRIPTION_GUIDANCE },
        ],
      },
      // In the legacy funnel the selection is the first onboarding step after
      // signup, so there is no previous screen to go back to. Funnel users
      // re-enter the pre-signup assessment only until account creation.
      back: [
        {
          target: ASSESSMENT_ROUTE.ASSESSMENT_SELF_TEST_PRE_SIGNUP_COMPLETE,
          when: ({ flags, user }) => flags.webOptimizedFunnel && user == null,
        },
      ],
    },
  },
  {
    path: '/insurance/covered',
    name: ROUTE.INSURANCE_COVERED,
    component: () => import('@eu/app/views/InsuranceCovered.vue'),
    beforeEnter: [requireCorporate],
    meta: {
      public: true,
      name: 'insurance_covered',
      onboardingStep: ({ isPreSubscriptionWebOptimizedFunnel }) =>
        isPreSubscriptionWebOptimizedFunnel ? -2 : 2,
      // NAME_INPUT is a pre-signup step — only route there for unauthenticated
      // funnel users. Authenticated users (e.g. arriving via back navigation)
      // continue to their actual next onboarding step: the corporate entries
      // mirror the guards' routeForCorporate decision (parity locked in
      // routes.spec.js); the corporate-null fallback is a defensive restart
      // (guard-dead via requireCorporate).
      next: [
        {
          target: ROUTE.NAME_INPUT,
          when: ({ flags, user }) => flags.webOptimizedFunnel && user == null,
        },
        {
          target: ROUTE.INSURANCE_VERIFICATION_PRIVATE,
          when: ({ corporate }) =>
            Boolean(corporate?.isDemo || corporate?.isPrivateWithContract),
        },
        {
          target: ROUTE.INSURANCE_OVERVIEW,
          when: ({ corporate }) => Boolean(corporate?.isPrivateInsurance),
        },
        {
          target: ROUTE.PRESCRIPTION_QUESTION,
          when: ({ corporate, flags }) =>
            corporate != null && flags.webOptimizedFunnel,
        },
        {
          target: ROUTE.CODE_QUESTION,
          when: ({ corporate }) => corporate != null,
        },
        { target: ROUTE.INSURANCE_SELECTION },
      ],
      back: [{ target: ROUTE.INSURANCE_SELECTION }],
    },
  },
  {
    path: '/insurance/overview',
    name: ROUTE.INSURANCE_OVERVIEW,
    component: () => import('@eu/app/views/InsuranceOverview.vue'),
    beforeEnter: [requirePrivateInsurance],
    meta: {
      name: 'signup_steps',
      onboardingStep: 8,
      next: {
        clarifyCosts: [{ target: ROUTE.INSURANCE_DETAILS }],
        requestBill: [{ target: ROUTE.INSURANCE_PRESCRIPTION_PRIVATE }],
        verifyCode: [{ target: ROUTE.INSURANCE_VERIFICATION_PRIVATE }],
      },
      back: [
        {
          target: ROUTE.INSURANCE_COVERED,
          // In the optimized funnel, this is the first post-signup screen.
          when: ({ isWebOptimizedFunnelOrResubscription }) =>
            !isWebOptimizedFunnelOrResubscription,
        },
      ],
    },
  },
  {
    path: '/insurance/details',
    name: ROUTE.INSURANCE_DETAILS,
    component: () => import('@eu/app/views/InsuranceDetails.vue'),
    beforeEnter: [requirePrivateInsurance],
    meta: {
      onboardingStep: 10,
      back: [{ target: ROUTE.INSURANCE_OVERVIEW }],
    },
  },
  {
    path: '/insurance/prescription/private',
    name: ROUTE.INSURANCE_PRESCRIPTION_PRIVATE,
    component: () => import('@eu/app/views/InsurancePrescriptionPrivate.vue'),
    beforeEnter: [requirePrivateInsurance],
    meta: {
      onboardingStep: 10,
      back: [{ target: ROUTE.INSURANCE_OVERVIEW }],
    },
  },
  {
    path: '/insurance/verification/private',
    name: ROUTE.INSURANCE_VERIFICATION_PRIVATE,
    component: () => import('@eu/app/views/CodeInput.vue'),
    beforeEnter: [skipToHukVerification],
    meta: {
      name: 'unified_code_input',
      onboardingStep: 9,
      next: {
        activated: [{ target: ROUTE.DOWNLOAD_LINK }],
        pzn: [{ target: ROUTE.PRESCRIPTION_UPLOAD }],
      },
      back: [
        {
          target: ROUTE.INSURANCE_COVERED,
          when: ({ corporate, isWebOptimizedFunnelOrResubscription }) =>
            Boolean(corporate?.isDemo || corporate?.isPrivateWithContract) &&
            !isWebOptimizedFunnelOrResubscription,
        },
        {
          target: ROUTE.INSURANCE_OVERVIEW,
          when: ({ corporate }) =>
            corporate != null &&
            !corporate.isDemo &&
            !corporate.isPrivateWithContract,
        },
      ],
    },
  },
  {
    path: '/insurance/verification/huk',
    name: ROUTE.INSURANCE_VERIFICATION_HUK,
    component: () => import('@eu/app/views/InsuranceVerificationHUK.vue'),
    meta: {
      name: 'huk_verification',
      onboardingStep: 9,
      back: [
        {
          target: ROUTE.INSURANCE_COVERED,
          when: ({ isWebOptimizedFunnelOrResubscription }) =>
            !isWebOptimizedFunnelOrResubscription,
        },
      ],
    },
  },
  {
    path: '/prescription/check',
    name: ROUTE.PRESCRIPTION_CHECK,
    component: () => import('@eu/app/views/PrescriptionCheck.vue'),
    beforeEnter: [requireStrictlyPublicInsurance],
    meta: {
      name: 'signup_diga_prescription_start',
      onboardingStep: 5,
      next: {
        hasPrescription: [
          {
            target: ROUTE.PRESCRIPTION_UPLOAD,
            when: ({ corporate }) =>
              corporate?.prescriptionUploadMode === 'standard',
          },
          { target: ROUTE.PRESCRIPTION_INSURANCE },
        ],
        noPrescription: [{ target: ROUTE.PRESCRIPTION_APPOINTMENT }],
        supportSkipped: [
          {
            target: ROUTE.DOWNLOAD_LINK,
            when: ({ canReturnToApp, diga }) => canReturnToApp || !diga,
          },
          { target: ROUTE.PRESCRIPTION_GUIDANCE },
        ],
      },
      back: [{ target: ROUTE.INSURANCE_SEARCH_PRIMER }],
    },
  },
  {
    // this is a deeplink target
    path: '/prescription/activation',
    name: ROUTE.PRESCRIPTION_ACTIVATION,
    component: () => import('@eu/app/views/CodeInput.vue'),
    beforeEnter: [requireDigaBuild, requireStrictlyPublicInsurance],
    meta: {
      name: 'unified_code_input',
      onboardingStep: 9,
      next: {
        activated: [{ target: ROUTE.DOWNLOAD_LINK }],
        pzn: [{ target: ROUTE.PRESCRIPTION_UPLOAD }],
      },
    },
  },
  {
    // this is a deeplink target
    path: '/prescription/appointment',
    name: ROUTE.PRESCRIPTION_APPOINTMENT,
    component: () => import('@eu/app/views/PrescriptionAppointment.vue'),
    beforeEnter: [requireDigaBuild, requireSubscription],
    meta: {
      name: 'signup_diga_prescription_appointment',
      onboardingStep: 9,
      next: {
        hasAppointment: [
          {
            target: ROUTE.DOWNLOAD_LINK,
            when: ({ canReturnToApp }) => canReturnToApp,
          },
          { target: ROUTE.PRESCRIPTION_GUIDANCE },
        ],
        supportSkipped: [
          {
            target: ROUTE.DOWNLOAD_LINK,
            when: ({ canReturnToApp, diga }) => canReturnToApp || !diga,
          },
          { target: ROUTE.PRESCRIPTION_GUIDANCE },
        ],
      },
      back: [{ target: ROUTE.PRESCRIPTION_CHECK }],
    },
  },
  {
    path: '/prescriber/information',
    name: ROUTE.PRESCRIBER_INFORMATION,
    component: () => import('@eu/app/views/PrescriberInformation.vue'),
    meta: {
      name: 'signup_hcp_info',
      onboardingStep: 8,
      next: [{ target: ROUTE.PHONE_INPUT_PRESCRIBER }],
      back: [{ target: ROUTE.PRESCRIPTION_INSURANCE }],
    },
  },
  {
    // this is a deeplink target
    path: '/prescription/upload',
    name: ROUTE.PRESCRIPTION_UPLOAD,
    component: () => import('@eu/app/views/PrescriptionUpload.vue'),
    beforeEnter: [requireStrictlyPublicInsurance],
    meta: {
      name: 'signup_prescription_upload',
      onboardingStep: 6,
      next: {
        uploaded: [{ target: ROUTE.PHONE_INPUT }],
        selfSubmit: [{ target: ROUTE.PRESCRIPTION_INSURANCE }],
        supportSkipped: [
          {
            target: ROUTE.DOWNLOAD_LINK,
            when: ({ canReturnToApp, diga }) => canReturnToApp || !diga,
          },
          { target: ROUTE.PRESCRIPTION_GUIDANCE },
        ],
      },
      back: [
        {
          target: ROUTE.CODE_QUESTION,
          when: ({ isResubscription }) => isResubscription,
        },
        {
          target: ROUTE.PRESCRIPTION_QUESTION,
          when: ({ flags }) => flags.webOptimizedFunnel,
        },
        { target: ROUTE.PRESCRIPTION_CHECK },
      ],
    },
  },
  {
    path: '/prescription/insurance',
    name: ROUTE.PRESCRIPTION_INSURANCE,
    component: () => import('@eu/app/views/PrescriptionInsurance.vue'),
    beforeEnter: [requireStrictlyPublicInsurance],
    meta: {
      name: 'signup_get_diga_prescription_info',
      onboardingStep: 7,
      // prescriber info on first visit, then the phone-input convergence
      next: [
        {
          target: ROUTE.PRESCRIBER_INFORMATION,
          when: ({ prescriberInformationShown }) => !prescriberInformationShown,
        },
        { target: ROUTE.PHONE_INPUT_INSURANCE },
      ],
      // Reached from PRESCRIPTION_UPLOAD ("submit myself"), except in the
      // legacy funnel with non-standard upload mode where PRESCRIPTION_CHECK
      // links here directly (mirrors PRESCRIPTION_CHECK's next).
      back: [
        {
          target: ROUTE.PRESCRIPTION_UPLOAD,
          when: ({ isWebOptimizedFunnelOrResubscription }) =>
            isWebOptimizedFunnelOrResubscription,
        },
        {
          target: ROUTE.PRESCRIPTION_CHECK,
          when: ({ corporate }) =>
            corporate?.prescriptionUploadMode !== 'standard',
        },
        { target: ROUTE.PRESCRIPTION_UPLOAD },
      ],
    },
  },
  {
    // this is a deeplink target
    path: '/prescription/guidance',
    name: ROUTE.PRESCRIPTION_GUIDANCE,
    component: () => import('@eu/app/views/PrescriptionGuidance.vue'),
    beforeEnter: [requireDigaBuild, requireSubscription],
    meta: {
      name: 'signup_get_diga_prescription_info',
      onboardingStep: 10,
      back: [{ target: ROUTE.PRESCRIPTION_CHECK }],
    },
  },
  {
    path: '/phone-input',
    name: ROUTE.PHONE_INPUT,
    component: PhoneInputView,
    // Users who already provided a phone number skip straight to the download
    // link; in-app users do enter their number first and only then exit to the
    // app (via returnToApp on the download routes)
    beforeEnter: [skipWhenPhoneProvided],
    meta: {
      name: 'phone_input',
      onboardingStep: 9,
      next: [{ target: ROUTE.DOWNLOAD_LINK }],
      back: [{ target: ROUTE.PRESCRIPTION_UPLOAD }],
    },
  },
  {
    path: '/phone-input/insurance',
    name: ROUTE.PHONE_INPUT_INSURANCE,
    component: PhoneInputView,
    beforeEnter: [skipWhenPhoneProvided],
    meta: {
      name: 'phone_input',
      onboardingStep: 9,
      next: [{ target: ROUTE.DOWNLOAD_LINK }],
      back: [{ target: ROUTE.PRESCRIPTION_INSURANCE }],
    },
  },
  {
    path: '/phone-input/prescriber',
    name: ROUTE.PHONE_INPUT_PRESCRIBER,
    component: PhoneInputView,
    beforeEnter: [skipWhenPhoneProvided],
    meta: {
      name: 'phone_input',
      onboardingStep: 9,
      next: [{ target: ROUTE.DOWNLOAD_LINK }],
      back: [{ target: ROUTE.PRESCRIBER_INFORMATION }],
    },
  },
  {
    path: '/phone-input/outreach',
    name: ROUTE.PHONE_INPUT_OUTREACH,
    component: PhoneInputView,
    beforeEnter: [skipWhenPhoneProvided],
    meta: {
      name: 'phone_input',
      onboardingStep: 9,
      next: [{ target: ROUTE.DOWNLOAD_LINK }],
      back: [{ target: ROUTE.DOCTOR_OUTREACH_CONSENT }],
    },
  },
  {
    path: '/phone-input/outreach/manual',
    name: ROUTE.PHONE_INPUT_OUTREACH_MANUAL,
    component: PhoneInputView,
    beforeEnter: [skipWhenPhoneProvided],
    meta: {
      name: 'phone_input',
      onboardingStep: 9,
      next: [{ target: ROUTE.DOWNLOAD_LINK }],
      back: [{ target: ROUTE.DOCTOR_OUTREACH_CONSENT_MANUAL }],
    },
  },
  {
    path: '/phone-input/outreach/confirmation',
    name: ROUTE.PHONE_INPUT_OUTREACH_CONFIRMATION,
    component: PhoneInputView,
    beforeEnter: [skipWhenPhoneProvided],
    meta: {
      name: 'phone_input',
      onboardingStep: 9,
      next: [{ target: ROUTE.DOWNLOAD_LINK }],
      back: [{ target: ROUTE.DOCTOR_OUTREACH_CONSENT_CONFIRMATION }],
    },
  },
  {
    path: '/prescription/support',
    name: ROUTE.PRESCRIPTION_SUPPORT,
    component: () => import('@eu/app/views/PrescriptionSupport.vue'),
    // Direct entry only; in-flow support uses a modal.
    meta: {
      name: 'signup_get_diga_prescription_support',
      onboardingStep: 9,
      next: [
        {
          target: ROUTE.DOWNLOAD_LINK,
          when: ({ canReturnToApp, diga }) => canReturnToApp || !diga,
        },
        { target: ROUTE.PRESCRIPTION_GUIDANCE },
      ],
    },
  },
  {
    path: '/download_link',
    name: ROUTE.DOWNLOAD_LINK,
    component: () => import('@eu/app/views/DownloadLink.vue'),
    beforeEnter: [returnToApp, skipToContextualDownload],
    meta: {
      name: 'signup_app_download',
      onboardingStep: 10,
    },
  },
  {
    path: '/contextual_download_link',
    name: ROUTE.CONTEXTUAL_DOWNLOAD_LINK,
    component: () => import('@eu/app/views/ContextualDownloadLink.vue'),
    beforeEnter: [returnToApp, skipToLegacyDownload],
    meta: {
      name: 'signup_app_download_contextual',
      onboardingStep: 10,
    },
  },
  {
    path: '/password-reset',
    name: ROUTE.PASSWORD_RESET,
    component: () => import('@shared/views/PasswordReset.vue'),
    meta: { public: true },
  },
  {
    path: '/deeplink-fallback',
    name: ROUTE.DEEPLINK_FALLBACK,
    component: () => import('@shared/views/DeeplinkFallback.vue'),
    meta: { public: true },
  },
  {
    path: '/session-expired',
    name: ROUTE.SESSION_EXPIRED,
    component: () => import('@shared/components/SessionExpired.vue'),
    meta: { public: true },
  },
  {
    path: '/prescription/service/primer',
    name: ROUTE.DOCTOR_OUTREACH_PRIMER,
    component: () => import('@eu/app/views/DoctorOutreachPrimer.vue'),
    beforeEnter: [requireStrictlyPublicInsurance],
    meta: {
      name: 'doctor_outreach_primer',
      onboardingStep: ({ isResubscription }) => (isResubscription ? 4 : 6),
      next: {
        needsPrescription: [
          {
            target: ROUTE.DOCTOR_OUTREACH_PRACTICE_CONFIRMATION,
            when: ({
              hasResolvedPractice,
              isWebOptimizedFunnelOrResubscription,
            }) => hasResolvedPractice && isWebOptimizedFunnelOrResubscription,
          },
          {
            target: ROUTE.DOCTOR_OUTREACH_PRACTICE_CONFIRMATION,
            when: ({ hasResolvedPractice }) => hasResolvedPractice,
            viaAssessment: {
              progressFrom: ROUTE.DOCTOR_OUTREACH_PRIMER,
            },
          },
          {
            target: ROUTE.DOCTOR_OUTREACH_ADD_PRESCRIBER,
            when: ({ isWebOptimizedFunnelOrResubscription }) =>
              isWebOptimizedFunnelOrResubscription,
          },
          {
            target: ROUTE.DOCTOR_OUTREACH_ADD_PRESCRIBER,
            viaAssessment: {
              progressFrom: ROUTE.DOCTOR_OUTREACH_PRIMER,
            },
          },
        ],
        hasPrescription: [{ target: ROUTE.CODE_QUESTION }],
      },
      back: [
        {
          target: ROUTE.PRESCRIPTION_QUESTION,
          when: ({ flags, isResubscription }) =>
            flags.webOptimizedFunnel && !isResubscription,
        },
        {
          target: ROUTE.PRESCRIPTION_CHECK,
          when: ({ isResubscription }) => !isResubscription,
        },
      ],
    },
  },
  {
    path: '/prescription/service/practice-confirmation',
    name: ROUTE.DOCTOR_OUTREACH_PRACTICE_CONFIRMATION,
    component: () =>
      import('@eu/app/views/DoctorOutreachPracticeConfirmation.vue'),
    beforeEnter: [requireResolvedPractice],
    meta: {
      name: 'doctor_outreach_practice_confirmation',
      onboardingStep: 7,
      next: {
        confirmed: [{ target: ROUTE.DOCTOR_OUTREACH_CONSENT_CONFIRMATION }],
        changeDoctor: [{ target: ROUTE.DOCTOR_OUTREACH_ADD_PRESCRIBER }],
      },
      back: [{ target: ROUTE.DOCTOR_OUTREACH_PRIMER }],
    },
  },
  {
    path: '/prescription/service/prescriber-search',
    name: ROUTE.DOCTOR_OUTREACH_PRESCRIBER_SEARCH,
    component: () => import('@eu/app/views/DoctorOutreachPrescriberSearch.vue'),
    meta: {
      name: 'doctor_outreach_prescriber_search',
      onboardingStep: 7,
      next: {
        prescriberSelected: [{ target: ROUTE.DOCTOR_OUTREACH_CONSENT }],
        manualEntry: [{ target: ROUTE.DOCTOR_OUTREACH_ADD_PRESCRIBER }],
      },
      back: [{ target: ROUTE.DOCTOR_OUTREACH_ADD_PRESCRIBER }],
    },
  },
  {
    path: '/prescription/service/add-prescriber',
    name: ROUTE.DOCTOR_OUTREACH_ADD_PRESCRIBER,
    component: () => import('@eu/app/views/DoctorOutreachAddPrescriber.vue'),
    meta: {
      name: 'doctor_outreach_add_prescriber',
      onboardingStep: 7,
      next: {
        prescriberEntered: [{ target: ROUTE.DOCTOR_OUTREACH_CONSENT_MANUAL }],
        searchPrescriber: [{ target: ROUTE.DOCTOR_OUTREACH_PRESCRIBER_SEARCH }],
      },
      back: [
        {
          target: ROUTE.DOCTOR_OUTREACH_PRACTICE_CONFIRMATION,
          when: ({ hasResolvedPractice }) => hasResolvedPractice,
        },
        { target: ROUTE.DOCTOR_OUTREACH_PRIMER },
      ],
    },
  },
  {
    path: '/prescription/service/consent',
    name: ROUTE.DOCTOR_OUTREACH_CONSENT,
    component: DoctorOutreachConsentView,
    meta: {
      name: 'doctor_outreach_consent',
      onboardingStep: 8,
      next: [{ target: ROUTE.PHONE_INPUT_OUTREACH }],
      back: [{ target: ROUTE.DOCTOR_OUTREACH_PRESCRIBER_SEARCH }],
    },
  },
  {
    path: '/prescription/service/consent/manual',
    name: ROUTE.DOCTOR_OUTREACH_CONSENT_MANUAL,
    component: DoctorOutreachConsentView,
    meta: {
      name: 'doctor_outreach_consent',
      onboardingStep: 8,
      next: [{ target: ROUTE.PHONE_INPUT_OUTREACH_MANUAL }],
      back: [{ target: ROUTE.DOCTOR_OUTREACH_ADD_PRESCRIBER }],
    },
  },
  {
    path: '/prescription/service/consent/confirmation',
    name: ROUTE.DOCTOR_OUTREACH_CONSENT_CONFIRMATION,
    component: DoctorOutreachConsentView,
    meta: {
      name: 'doctor_outreach_consent',
      onboardingStep: 8,
      next: [{ target: ROUTE.PHONE_INPUT_OUTREACH_CONFIRMATION }],
      back: [{ target: ROUTE.DOCTOR_OUTREACH_PRACTICE_CONFIRMATION }],
    },
  },
] as const satisfies readonly AppRouteRecord[]

/** One record of the EU route table, literal types preserved — the source of
 *  the per-route intent unions (see RouteIntentArgs in useNavigation.ts). */
export type EuRoute = (typeof routes)[number]

registerOnboardingSteps(routes)

export default routes
